import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import ForwardToInboxRoundedIcon from '@mui/icons-material/ForwardToInboxRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import InstagramIcon from '@mui/icons-material/Instagram'
import {
  downloadEnrichedCsv,
  fetchBnbBatches,
  fetchBnbLogs,
  fetchBnbRows,
  fetchBnbStatus,
  promoteBnbToImport,
  runBnbEnrichment,
  stopBnbEnrichment,
  uploadBnbRawCsv,
} from '../services/adminApi'
import { PageShell, StatCard, TerminalLogs } from '../components/shared'
import { useAdminJob } from '../AdminConsole'

type BnbBatch = {
  batch_uuid: string
  original_filename: string
  row_count: number
  status: string
  enriched_count: number
  failed_count: number
  skipped_count: number
  job_state?: string
  import_batch_uuid?: string
  remaining?: number
  row_status?: Record<string, number>
  job_thread_alive?: boolean
}

type SampleRow = {
  row_index: number
  title: string
  enrichment_status: string
  phone_found?: string
  instagram_url?: string
  emails_found?: string
  enrichment_notes?: string
}

export default function BnbEnrichmentPage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { setJobRunning } = useAdminJob()
  const [batches, setBatches] = useState<BnbBatch[]>([])
  const [batchUuid, setBatchUuid] = useState('')
  const [detail, setDetail] = useState<BnbBatch | null>(null)
  const [sampleRows, setSampleRows] = useState<SampleRow[]>([])
  const [logs, setLogs] = useState<{ id: number; level: string; message: string; created_at: string }[]>([])
  const [chunkSize, setChunkSize] = useState(3)
  const [aiDelay, setAiDelay] = useState(2)
  const [uploading, setUploading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const logEndRef = useRef<HTMLDivElement>(null)

  const refreshBatches = useCallback(async () => {
    const json = await fetchBnbBatches()
    const list = (json.batches || []) as BnbBatch[]
    setBatches(list)
    if (!batchUuid && list.length > 0) setBatchUuid(list[0].batch_uuid)
  }, [batchUuid])

  const refreshStatus = useCallback(async () => {
    if (!batchUuid) return
    const [st, lg, rows] = await Promise.all([
      fetchBnbStatus(batchUuid),
      fetchBnbLogs(batchUuid),
      fetchBnbRows(batchUuid, 200),
    ])
    setDetail(st.batch as BnbBatch)
    setLogs(lg.logs || [])
    setSampleRows(rows.rows || [])
  }, [batchUuid])

  useEffect(() => {
    refreshBatches().catch(() => {})
  }, [refreshBatches])

  useEffect(() => {
    if (!batchUuid) return
    refreshStatus().catch(() => {})
    const id = window.setInterval(() => refreshStatus().catch(() => {}), 5000)
    return () => window.clearInterval(id)
  }, [batchUuid, refreshStatus])

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs])

  const rs = detail?.row_status || {}
  const total = detail?.row_count ?? 0
  const done = (rs.done ?? 0) + (rs.skipped ?? 0)
  const failed = rs.failed ?? detail?.failed_count ?? 0
  const pending = detail?.remaining ?? rs.pending ?? 0
  const progress = total > 0 ? Math.round((done / total) * 100) : 0
  const jobState = detail?.job_state || 'idle'
  const isRunning = jobState === 'running' || jobState === 'stopping' || detail?.job_thread_alive

  useEffect(() => {
    setJobRunning(Boolean(isRunning))
    return () => setJobRunning(false)
  }, [isRunning, setJobRunning])

  const handleUpload = async (file: File) => {
    setUploading(true)
    setFlash(null)
    try {
      const json = await uploadBnbRawCsv(file)
      setBatchUuid(json.batch_uuid)
      setFlash({ type: 'success', text: `Loaded ${json.row_count} BNB rows for enrichment` })
      await refreshBatches()
      await refreshStatus()
    } catch (e) {
      setFlash({ type: 'error', text: e instanceof Error ? e.message : 'Upload failed' })
    } finally {
      setUploading(false)
    }
  }

  return (
    <PageShell
      title="BNB Contact Enrichment"
      subtitle="Upload raw Google Maps CSV → AI web search finds Instagram, phone & email per listing (batch mode, low DB load)"
    >
      {flash && (
        <Alert severity={flash.type} sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setFlash(null)}>
          {flash.text}
        </Alert>
      )}

      {detail && (
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 6, sm: 3 }}><StatCard label="Listings" value={total} accent="slate" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><StatCard label="Enriched" value={done} accent="green" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><StatCard label="Pending" value={pending} accent="amber" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><StatCard label="Failed" value={failed} accent={failed ? 'rose' : 'slate'} /></Grid>
        </Grid>
      )}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, xl: 6 }}>
          <Paper className="admin-panel" elevation={0} sx={{ mb: 2.5 }}>
            <Box className="admin-panel-header">
              <Typography variant="h6" fontWeight={700}>Raw BNB CSV</Typography>
              <Typography variant="body2" color="text.secondary">Same format as bnbs-raw/ Google scrape exports</Typography>
            </Box>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <input ref={fileRef} type="file" accept=".csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = '' }} />
              <Box className="admin-upload-zone" onClick={() => !uploading && fileRef.current?.click()} role="button" tabIndex={0}>
                {uploading ? <CircularProgress size={32} sx={{ color: '#C9A227' }} /> : <CloudUploadRoundedIcon sx={{ fontSize: 40, color: '#C9A227', mb: 1 }} />}
                <Typography fontWeight={600}>{uploading ? 'Uploading…' : 'Upload raw BNB CSV'}</Typography>
                <Typography variant="caption" color="text.secondary">title · images · owner · phone · address columns preserved</Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
                <TextField select fullWidth size="small" label="Enrichment batch" value={batchUuid} onChange={(e) => setBatchUuid(e.target.value)}>
                  <MenuItem value="">Select…</MenuItem>
                  {batches.map((b) => (
                    <MenuItem key={b.batch_uuid} value={b.batch_uuid}>
                      {b.original_filename} · {b.row_count} · {b.status}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton onClick={() => refreshBatches()} sx={{ border: '1px solid rgba(20,24,32,0.1)', borderRadius: 2 }}>
                  <RefreshRoundedIcon />
                </IconButton>
              </Stack>

              {detail && (
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Chip label={jobState} size="small" color={isRunning ? 'warning' : detail.status === 'completed' ? 'success' : 'default'} />
                    <Typography variant="caption" fontWeight={600}>{progress}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 999 }} />
                </Box>
              )}

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={6}>
                  <TextField type="number" fullWidth size="small" label="Batch size (rows/chunk)" value={chunkSize}
                    onChange={(e) => setChunkSize(Math.min(10, Math.max(1, Number(e.target.value) || 3)))} helperText="Small chunks = fewer DB connections" />
                </Grid>
                <Grid size={6}>
                  <TextField type="number" fullWidth size="small" label="AI delay (sec)" value={aiDelay}
                    onChange={(e) => setAiDelay(Math.min(10, Math.max(0.5, Number(e.target.value) || 2)))} helperText="Pause between Napps AI calls" />
                </Grid>
              </Grid>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2.5 }}>
                <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} disabled={!batchUuid || isRunning || busy}
                  onClick={async () => {
                    setBusy(true)
                    try {
                      await runBnbEnrichment(batchUuid, { chunk_size: chunkSize, ai_delay_seconds: aiDelay })
                      setFlash({ type: 'info', text: 'Enrichment running in background' })
                      await refreshStatus()
                    } catch (e) {
                      setFlash({ type: 'error', text: e instanceof Error ? e.message : 'Start failed' })
                    } finally {
                      setBusy(false)
                    }
                  }}>
                  Start enrichment
                </Button>
                <Button variant="outlined" startIcon={<StopRoundedIcon />} disabled={!isRunning}
                  onClick={async () => { await stopBnbEnrichment(batchUuid); refreshStatus() }}>
                  Stop
                </Button>
                <Button variant="outlined" startIcon={<DownloadRoundedIcon />} disabled={!batchUuid || detail?.status !== 'completed'}
                  onClick={() => downloadEnrichedCsv(batchUuid)}>
                  Download CSV
                </Button>
                <Button variant="outlined" color="secondary" startIcon={<ForwardToInboxRoundedIcon />} disabled={!batchUuid || pending > 0}
                  onClick={async () => {
                    setBusy(true)
                    try {
                      const r = await promoteBnbToImport(batchUuid)
                      setFlash({ type: 'success', text: `Ready in CSV Import → batch ${r.import_batch_uuid}` })
                      await refreshStatus()
                    } catch (e) {
                      setFlash({ type: 'error', text: e instanceof Error ? e.message : 'Promote failed' })
                    } finally {
                      setBusy(false)
                    }
                  }}>
                  Send to import
                </Button>
              </Stack>

              {detail?.import_batch_uuid && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                  Linked import batch: <code>{detail.import_batch_uuid}</code> — open CSV Import in sidebar
                </Alert>
              )}
            </Box>
          </Paper>

          <Paper className="admin-panel" elevation={0}>
            <Box className="admin-panel-header"><Typography variant="h6" fontWeight={700}>Enrichment logs</Typography></Box>
            <Box sx={{ p: 2 }}>
              <TerminalLogs title="bnb-enrichment" logs={logs} logEndRef={logEndRef} />
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, xl: 6 }}>
          <Paper className="admin-panel" elevation={0}>
            <Box className="admin-panel-header">
              <Typography variant="h6" fontWeight={700}>Row preview</Typography>
              <Typography variant="caption" color="text.secondary">Every listing · mobile · email · Instagram</Typography>
            </Box>
            <Box className="admin-table-scroll" sx={{ maxHeight: { xs: 420, lg: 640 } }}>
              <Table size="small" stickyHeader sx={{ minWidth: 720, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell width={40} sx={{ bgcolor: '#faf8f5' }}>#</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5', width: '28%' }}>Listing</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5', width: 90 }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5', width: '18%' }}>Mobile</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5', width: '20%' }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5', width: 80 }}>Instagram</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sampleRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>Upload CSV to preview rows</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {sampleRows.map((row) => (
                    <TableRow key={row.row_index} hover>
                      <TableCell>{row.row_index + 1}</TableCell>
                      <TableCell sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={row.title}>
                          {row.title || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.enrichment_status || 'pending'} size="small" variant="outlined" sx={{ maxWidth: '100%' }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap display="block">{row.phone_found || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap display="block">{row.emails_found || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {row.instagram_url ? (
                          <Button size="small" href={row.instagram_url} target="_blank" rel="noopener" startIcon={<InstagramIcon />} sx={{ minWidth: 0, p: 0, fontSize: 11 }}>
                            IG
                          </Button>
                        ) : (
                          <Typography variant="caption">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {isMobile && (
        <Box className="admin-mobile-bar">
          <Button fullWidth variant="contained" disabled={!batchUuid || isRunning} startIcon={<PlayArrowRoundedIcon />}
            onClick={async () => runBnbEnrichment(batchUuid, { chunk_size: chunkSize, ai_delay_seconds: aiDelay })}>
            Enrich
          </Button>
        </Box>
      )}
    </PageShell>
  )
}
